/**
 * Catalogue items (cosmetic_items) — liste filtrable + édition + preview simulate.
 * SPEC_GESTIONNAIRE_ITEMS C-2 · full admin only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory2 as CatalogIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Science as SimulateIcon,
} from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import NouvelObjetPorteDialog from '../../components/admin/NouvelObjetPorteDialog';
import { AdminService } from '../../services/adminService';
import { API_URL } from '../../utils/axios-config';

/** Objets portés par le compagnon : leur dessin se reprend depuis l'édition. */
const TYPES_PORTES = ['companion_head', 'companion_accessory', 'companion_hand'];
/** Les chemins servis sont complets (`/assets/…`) : `API_URL` devant, pas `getAssetUrl`. */
const urlServie = (chemin) => (chemin ? `${API_URL}${chemin}` : null);

const RARITY_COLOR = {
  common: 'default',
  rare: 'info',
  epic: 'secondary',
  legendary: 'warning',
};

const emptyForm = () => ({
  name: '',
  description: '',
  icon: '',
  type: '',
  subType: '',
  uiKind: '',
  rarity: 'common',
  price: 0,
  levelRequired: 0,
  shopPurchasable: false,
  active: true,
  seriesId: '',
  seriesOrder: '',
  homeSurface: '',
  grantSourcesText: '[]',
  effectsText: '[]',
});

function effectsLabel(effects) {
  if (!effects) return '—';
  const list = Array.isArray(effects) ? effects : [];
  if (list.length === 0) return '—';
  return list
    .map((e) => {
      if (e.kind === 'xp_percent') return `+${e.percent}% XP`;
      if (e.kind === 'coins_percent') return `+${e.percent}% 🪙`;
      if (e.kind === 'flat_xp_on_unlock') return `+${e.amount} XP`;
      if (e.kind === 'flat_coins_on_unlock') return `+${e.amount} 🪙`;
      if (e.kind === 'grant_item') return `→ ${e.itemId}`;
      return e.kind;
    })
    .join(', ');
}

export default function CatalogItemsScreen() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({
    types: [],
    series: [],
    rarities: ['common', 'rare', 'epic', 'legendary'],
    homeSurfaces: [],
    effectKinds: [],
  });

  const [filters, setFilters] = useState({
    type: '',
    rarity: '',
    series: '',
    active: '',
    q: '',
  });

  const [nouvelObjetOuvert, setNouvelObjetOuvert] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [dessinOuvert, setDessinOuvert] = useState(false);
  /** Ids dont la bascule actif/inactif est en vol (un clic à la fois par ligne). */
  const [basculesEnVol, setBasculesEnVol] = useState(() => new Set());
  const [form, setForm] = useState(emptyForm());
  const [simulatePreview, setSimulatePreview] = useState(null);
  const [sampleGain, setSampleGain] = useState({ xp: 100, coins: 50 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 300 };
      if (filters.type) params.type = filters.type;
      if (filters.rarity) params.rarity = filters.rarity;
      if (filters.series) params.series = filters.series;
      if (filters.active) params.active = filters.active;
      if (filters.q.trim()) params.q = filters.q.trim();

      const data = await AdminService.getCatalogItems(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total ?? 0);
      if (data?.meta) {
        setMeta((m) => ({ ...m, ...data.meta }));
      }
    } catch (e) {
      setError(e?.message || 'Erreur chargement catalogue');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (item) => {
    setEditId(item.id);
    setEditItem(item);
    setSuccess(null);
    setSimulatePreview(null);
    setForm({
      name: item.name || '',
      description: item.description || '',
      icon: item.icon || '',
      type: item.type || '',
      subType: item.subType || '',
      uiKind: item.uiKind || '',
      rarity: item.rarity || 'common',
      price: item.price ?? 0,
      levelRequired: item.levelRequired ?? 0,
      shopPurchasable: Boolean(item.shopPurchasable),
      active: item.active !== false,
      seriesId: item.seriesId || '',
      seriesOrder: item.seriesOrder ?? '',
      homeSurface: item.homeSurface || '',
      grantSourcesText: JSON.stringify(item.grantSources || [], null, 2),
      effectsText: JSON.stringify(item.effects || [], null, 2),
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditId(null);
    setEditItem(null);
    setSimulatePreview(null);
  };

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const runSimulate = async () => {
    setError(null);
    try {
      const effects = JSON.parse(form.effectsText || '[]');
      const data = await AdminService.simulateCatalogEffects({
        effects,
        sampleGain,
      });
      setSimulatePreview(data);
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Simulate impossible');
      setSimulatePreview(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      let effects;
      let grantSources;
      try {
        effects = JSON.parse(form.effectsText || '[]');
      } catch {
        throw new Error('effects : JSON invalide');
      }
      try {
        grantSources = JSON.parse(form.grantSourcesText || '[]');
      } catch {
        throw new Error('grantSources : JSON invalide');
      }

      const payload = {
        name: form.name,
        description: form.description || null,
        icon: form.icon || null,
        type: form.type,
        subType: form.subType || null,
        uiKind: form.uiKind || null,
        rarity: form.rarity,
        price: Number(form.price),
        levelRequired: Number(form.levelRequired),
        shopPurchasable: form.shopPurchasable,
        active: form.active,
        seriesId: form.seriesId || null,
        seriesOrder: form.seriesOrder === '' ? null : Number(form.seriesOrder),
        homeSurface: form.homeSurface || null,
        grantSources,
        effects,
      };

      const data = await AdminService.updateCatalogItem(editId, payload);
      setSuccess(`« ${data?.item?.name || editId} » enregistré`);
      if (data?.simulate) setSimulatePreview(data.simulate);
      await load();
    } catch (e) {
      const details = e?.data?.details;
      const msg = e?.data?.error || e?.message || 'Erreur enregistrement';
      setError(details?.length ? `${msg} — ${details.join('; ')}` : msg);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actif ↔ inactif d'un clic dans le tableau, sans ouvrir l'édition. La ligne
   * reste en place même si un filtre « Actif » ne la retiendrait plus : on peut
   * se reprendre d'un second clic.
   */
  const basculerActif = async (item) => {
    if (basculesEnVol.has(item.id)) return;
    const actif = !item.active;
    setError(null);
    setBasculesEnVol((s) => new Set(s).add(item.id));
    setItems((liste) => liste.map((i) => (i.id === item.id ? { ...i, active: actif } : i)));
    try {
      await AdminService.updateCatalogItem(item.id, { active: actif });
      setSuccess(`« ${item.name || item.id} » ${actif ? 'activé' : 'désactivé'}`);
    } catch (e) {
      setItems((liste) => liste.map((i) => (i.id === item.id ? { ...i, active: item.active } : i)));
      setError(e?.data?.error || e?.message || 'Bascule impossible');
    } finally {
      setBasculesEnVol((s) => {
        const reste = new Set(s);
        reste.delete(item.id);
        return reste;
      });
    }
  };

  /** Dessin repris : la liste (et l'aperçu de l'édition) montrent les nouveaux fichiers. */
  const dessinEnregistre = async (item) => {
    setDessinOuvert(false);
    setSuccess(`Dessin de « ${item?.name || editId} » enregistré — l'app le voit dans la minute.`);
    const data = await AdminService.getCatalogItems({ q: editId, limit: 50 }).catch(() => null);
    await load();
    const frais = data?.items?.find((i) => i.id === editId);
    if (frais) setEditItem(frais);
    else if (item) setEditItem((e) => ({ ...e, ...item }));
  };

  /** Ce que l'admin doit savoir en sortant : reste-t-il des boîtes à placer ? */
  const objetCree = async (item, placement) => {
    setNouvelObjetOuvert(false);
    setSuccess(placement?.herite
      ? `« ${item?.name || item?.id} » créé — il se pose tout seul là où est posé « ${placement.base} », sur tous les compagnons.`
      : `« ${item?.name || item?.id} » créé — reste à le placer dans tools/editeur-habillage.`);
    await load();
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CatalogIcon color="primary" />
          <Typography variant="h4">Catalogue items</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
          Édition de <code>cosmetic_items</code> (rareté, boutique, série, effects).
          Validation EffectEngine à la sauvegarde · preview simulate sur un gain type.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Recherche"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={filters.type}
                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
              >
                <MenuItem value="">Tous</MenuItem>
                {(meta.types || []).map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Rareté</InputLabel>
              <Select
                label="Rareté"
                value={filters.rarity}
                onChange={(e) => setFilters((f) => ({ ...f, rarity: e.target.value }))}
              >
                <MenuItem value="">Toutes</MenuItem>
                {(meta.rarities || []).map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Série</InputLabel>
              <Select
                label="Série"
                value={filters.series}
                onChange={(e) => setFilters((f) => ({ ...f, series: e.target.value }))}
              >
                <MenuItem value="">Toutes</MenuItem>
                {(meta.series || []).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Actif</InputLabel>
              <Select
                label="Actif"
                value={filters.active}
                onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="true">Oui</MenuItem>
                <MenuItem value="false">Non</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={loading}
            >
              Rafraîchir
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setSuccess(null); setNouvelObjetOuvert(true); }}
            >
              Nouvel objet
            </Button>
          </Stack>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {total} item{total === 1 ? '' : 's'}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell>Id</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Rareté</TableCell>
                  <TableCell align="right">Prix</TableCell>
                  <TableCell>Boutique</TableCell>
                  <TableCell>Série</TableCell>
                  <TableCell>Effets</TableCell>
                  <TableCell>Actif</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEdit(item)}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {item.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {item.inventoryImageUrl
                          ? <img src={urlServie(item.inventoryImageUrl)} alt="" loading="lazy" width={28} height={28} style={{ objectFit: 'contain' }} />
                          : <span>{item.icon || '·'}</span>}
                        <Typography variant="body2">{item.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{item.type}</Typography>
                      {item.uiKind && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.uiKind}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.rarity}
                        color={RARITY_COLOR[item.rarity] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{item.price ?? 0}</TableCell>
                    <TableCell>{item.shopPurchasable ? '✓' : '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {item.seriesId || item.subType || '—'}
                        {item.seriesOrder != null ? ` · #${item.seriesOrder}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{effectsLabel(item.effects)}</Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Chip
                        size="small"
                        label={item.active ? 'actif' : 'inactif'}
                        color={item.active ? 'success' : 'default'}
                        variant={item.active ? 'filled' : 'outlined'}
                        title={item.active ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                        disabled={basculesEnVol.has(item.id)}
                        onClick={() => basculerActif(item)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Aucun item (filtres trop stricts, ou migr. 095 non appliquée).
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={editOpen} onClose={closeEdit} maxWidth="md" fullWidth>
          <DialogTitle>
            Éditer · <code>{editId}</code>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {editItem && (editItem.inventoryImageUrl || editItem.imageUrl) && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    {[
                      ['Icône', editItem.inventoryImageUrl],
                      ['Dessin', editItem.imageUrl],
                      ['Devant', editItem.habillage?.arriere ? editItem.habillage?.image : null],
                      ['Derrière', editItem.habillage?.arriere],
                    ].filter(([, url]) => url).map(([libelle, url]) => (
                      <Box key={libelle} sx={{ textAlign: 'center' }}>
                        <Box
                          component="img" src={urlServie(url)} alt={libelle}
                          sx={{
                            width: 96, height: 96, objectFit: 'contain', display: 'block',
                            borderRadius: 1, border: '1px solid', borderColor: 'divider',
                            // Damier : on voit la transparence, donc la découpe.
                            backgroundImage: 'linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)',
                            backgroundSize: '12px 12px',
                            backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">{libelle}</Typography>
                      </Box>
                    ))}
                    {TYPES_PORTES.includes(editItem.type) && (
                      <Box sx={{ flex: 1, textAlign: { sm: 'right' } }}>
                        <Button variant="outlined" onClick={() => setDessinOuvert(true)}>
                          Dessin, découpe et cadre…
                        </Button>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Retoucher ce qui passe derrière, changer de cadre ou de fichier.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
                <TextField
                  label="Icône"
                  value={form.icon}
                  onChange={(e) => setField('icon', e.target.value)}
                  sx={{ width: 100 }}
                />
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Type"
                  value={form.type}
                  onChange={(e) => setField('type', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="subType"
                  value={form.subType}
                  onChange={(e) => setField('subType', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="uiKind"
                  value={form.uiKind}
                  onChange={(e) => setField('uiKind', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Rareté</InputLabel>
                  <Select
                    label="Rareté"
                    value={form.rarity}
                    onChange={(e) => setField('rarity', e.target.value)}
                  >
                    {(meta.rarities || []).map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Prix"
                  value={form.price}
                  onChange={(e) => setField('price', e.target.value)}
                  sx={{ width: 100 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Niv. requis"
                  value={form.levelRequired}
                  onChange={(e) => setField('levelRequired', e.target.value)}
                  sx={{ width: 110 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.shopPurchasable}
                      onChange={(e) => setField('shopPurchasable', e.target.checked)}
                    />
                  }
                  label="Boutique"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.active}
                      onChange={(e) => setField('active', e.target.checked)}
                    />
                  }
                  label="Actif"
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="seriesId"
                  value={form.seriesId}
                  onChange={(e) => setField('seriesId', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="seriesOrder"
                  type="number"
                  value={form.seriesOrder}
                  onChange={(e) => setField('seriesOrder', e.target.value)}
                  sx={{ width: 120 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>homeSurface</InputLabel>
                  <Select
                    label="homeSurface"
                    value={form.homeSurface}
                    onChange={(e) => setField('homeSurface', e.target.value)}
                  >
                    <MenuItem value="">—</MenuItem>
                    {(meta.homeSurfaces || []).map((h) => (
                      <MenuItem key={h} value={h}>{h}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="grantSources (JSON)"
                value={form.grantSourcesText}
                onChange={(e) => setField('grantSourcesText', e.target.value)}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={`effects (JSON) — kinds: ${(meta.effectKinds || []).join(', ')}`}
                value={form.effectsText}
                onChange={(e) => setField('effectsText', e.target.value)}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                helperText='Ex. [{"kind":"xp_percent","percent":10}]'
              />

              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview simulate
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    type="number"
                    label="XP base"
                    value={sampleGain.xp}
                    onChange={(e) => setSampleGain((s) => ({ ...s, xp: Number(e.target.value) }))}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Pièces base"
                    value={sampleGain.coins}
                    onChange={(e) => setSampleGain((s) => ({ ...s, coins: Number(e.target.value) }))}
                    sx={{ width: 110 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SimulateIcon />}
                    onClick={runSimulate}
                  >
                    Simuler
                  </Button>
                </Stack>
                {simulatePreview && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      XP {simulatePreview.before?.xp} → <strong>{simulatePreview.after?.xp}</strong>
                      {' · '}
                      🪙 {simulatePreview.before?.coins} → <strong>{simulatePreview.after?.coins}</strong>
                      {simulatePreview.percents && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}(+{simulatePreview.percents.xp || 0}% XP / +{simulatePreview.percents.coins || 0}% pièces)
                        </Typography>
                      )}
                    </Typography>
                    {(simulatePreview.summary || []).length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {(simulatePreview.summary || []).map((s, i) => (
                          <Chip key={i} size="small" label={`${s.icon || ''} ${s.label}`} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEdit}>Fermer</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={save}
              disabled={saving}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
        <NouvelObjetPorteDialog
          ouvert={dessinOuvert}
          objet={editItem}
          onFermer={() => setDessinOuvert(false)}
          onEnregistre={dessinEnregistre}
        />
        <NouvelObjetPorteDialog
          ouvert={nouvelObjetOuvert}
          onFermer={() => setNouvelObjetOuvert(false)}
          onCree={objetCree}
        />
      </Box>
    </AdminLayout>
  );
}
